import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CanonicalExam } from '../../core/model/canonical.ts';
import { questionAt } from '../../core/model/canonical.ts';
import type { Response } from '../../core/model/session.ts';
import type { SessionState } from '../../core/exam-engine/state.ts';
import type { SessionEvent } from '../../core/exam-engine/reducer.ts';
import {
  can,
  filterItems,
  isStopped,
  navigatorItems,
  nextMatching,
  progress,
  timeView,
  type NavigatorFilter,
} from '../../core/exam-engine/selectors.ts';
import { sourceNumber } from '../../core/exam-engine/blueprint.ts';
import { Calculator } from '../calculator/Calculator.tsx';
import { FocusOverlay } from './FocusOverlay.tsx';
import { QuestionNote } from './QuestionNote.tsx';
import { QUESTION_RENDERERS } from '../../questions/registry.tsx';
import { QuestionPrompt } from '../../questions/shared/QuestionParts.tsx';
import { useI18n } from '../../i18n/index.tsx';
import { Button, Callout, ExamText, LiveRegion } from '../../ui/primitives.tsx';
import { ReportQuestionDialog } from '../report-question/ReportQuestionDialog.tsx';

function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * The exam runner.
 *
 * Presentational: the session state and its dispatch are owned by the app shell. That is what lets
 * the route be *derived* from `state.status` — the results screen appears because the session is
 * complete, not because an effect pushed a navigation. One less way for the two to disagree.
 */
export function SessionScreen({
  exam,
  state,
  dispatch,
  onAbandon,
  onSave,
  focus,
  onExitFocus,
}: {
  readonly exam: CanonicalExam;
  readonly state: SessionState;
  readonly dispatch: (event: SessionEvent) => void;
  readonly onAbandon: () => void;
  /** Absent when this device is not remembering anything; the control is then not offered. */
  readonly onSave?: (() => boolean) | undefined;
  /** Whether the distraction-free view is open. Owned by the shell, since its button lives there. */
  readonly focus: boolean;
  readonly onExitFocus: () => void;
}) {
  const { t } = useI18n();
  const [reporting, setReporting] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [saveResult, setSaveResult] = useState<'saved' | 'failed' | null>(null);
  const [filter, setFilter] = useState<NavigatorFilter>('all');
  const [displayNow, setDisplayNow] = useState(() => performance.now());
  const headingRef = useRef<HTMLHeadingElement>(null);

  const view = timeView(state, displayNow);
  const counts = progress(state);
  const items = filterItems(navigatorItems(state), filter);
  const questionId = state.blueprint.questionOrder[state.cursor];
  const question = questionId === undefined ? undefined : questionAt(exam, questionId);
  const entry = questionId === undefined ? undefined : state.entries[questionId];

  /**
   * A one-second tick that only redraws and checks for expiry.
   *
   * Nothing is accumulated here — the duration is derived from the recorded segments — so a
   * throttled tick in a background tab costs a late redraw and nothing else.
   */
  useEffect(() => {
    if (state.status === 'completed') {
      return;
    }
    const handle = window.setInterval(() => {
      setDisplayNow(performance.now());
      dispatch({ type: 'TICK' });
    }, 1000);

    return () => {
      window.clearInterval(handle);
    };
  }, [state.status, dispatch]);

  useEffect(() => {
    const onVisibility = (): void => {
      dispatch({ type: 'VISIBILITY', visible: document.visibilityState === 'visible' });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [dispatch]);

  /**
   * Warn before leaving with a session in progress.
   *
   * Without persistence, a stray refresh loses an hour of work — the single worst thing this app
   * could do to someone. The browser decides the wording; all we can do is ask.
   */
  useEffect(() => {
    if (state.status === 'completed') {
      return;
    }
    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [state.status]);

  // Moving to a new question moves the focus with it, so a keyboard or screen-reader user is not
  // left at the top of a page that silently changed underneath them.
  useEffect(() => {
    headingRef.current?.focus();
  }, [state.cursor]);

  const answer = useCallback(
    (response: Response | null) => {
      if (questionId !== undefined) {
        dispatch({ type: 'ANSWER', questionId, response });
      }
    },
    [questionId, dispatch],
  );

  const Renderer = useMemo(
    () => (question === undefined ? null : QUESTION_RENDERERS[question.type]),
    [question],
  );

  if (question === undefined || questionId === undefined || entry === undefined) {
    return (
      <section className="screen">
        <Callout tone="warning">
          <p>{t('config.noMatches')}</p>
        </Callout>
        <Button onClick={onAbandon}>{t('config.back')}</Button>
      </section>
    );
  }

  const revealed = entry.revealedAt !== null;

  return (
    <section className="screen screen--session" aria-labelledby="session-heading">
      <header className="session__header">
        <div>
          <h1 id="session-heading" ref={headingRef} tabIndex={-1}>
            {t('session.question', { position: counts.position, total: counts.total })}
          </h1>
          {/* The number this question carries in the file. It survives filtering and shuffling, so
              it is the one number that means anything to whoever wrote the exam. */}
          <p className="session__source">
            {state.candidateName !== '' && (
              <>
                <span>{state.candidateName}</span>
                <span aria-hidden="true"> · </span>
              </>
            )}
            <span>{t('session.sourceNumber', { number: sourceNumber(exam, questionId) })}</span>
          </p>
        </div>

        {view.remainingMs !== null && (
          <p className={`timer${view.runningOut ? ' timer--urgent' : ''}`}>
            <span className="visually-hidden">{t('session.timeRemaining')}: </span>
            <span aria-hidden="true">{t('session.timeRemaining')}</span>{' '}
            <strong>{formatDuration(view.remainingMs)}</strong>
          </p>
        )}
      </header>

      {/* Announced at the minute, not every second: a timer that speaks constantly is unusable. */}
      <LiveRegion>
        {view.remainingMs !== null && view.remainingMs % 60_000 < 1000
          ? `${t('session.timeRemaining')}: ${formatDuration(view.remainingMs)}`
          : ''}
      </LiveRegion>

      {view.expired && (
        <Callout tone="warning">
          <p>{t('session.timeUp')}</p>
        </Callout>
      )}

      <div className="progress" role="presentation">
        <span
          className="progress__fill"
          style={{ inlineSize: `${String(Math.round((counts.position / Math.max(1, counts.total)) * 100))}%` }}
        />
      </div>

      {/* A pause hides the questions, which is the entire point of one: a stopped clock next to a
          readable exam is not a pause, it is unlimited time. */}
      {isStopped(state) ? (
        <div className="paused">
          <h2>{t('session.paused')}</h2>
          <p>{t('session.pausedHelp')}</p>
          <Button
            variant="primary"
            onClick={() => {
              dispatch({ type: 'RESUME' });
            }}
          >
            {t('session.resume')}
          </Button>
        </div>
      ) : (
      <article className="question">
        <div className="question__tools">
          {can(state, 'REVEAL') && (
            <Button
              variant="secondary"
              onClick={() => {
                dispatch({ type: 'REVEAL', questionId });
              }}
            >
              {t('session.checkAnswer')}
            </Button>
          )}
          <Button
            variant="quiet"
            aria-expanded={calculating}
            onClick={() => {
              setCalculating((open) => !open);
            }}
          >
            {t('calculator.title')}
          </Button>
        </div>

        <QuestionPrompt question={question} />

        {Renderer !== null && (
          <Renderer
            question={question}
            optionOrder={state.blueprint.optionOrder[questionId]}
            response={entry.response}
            onChange={answer}
            disabled={!can(state, 'ANSWER')}
            revealed={revealed}
          />
        )}

        <QuestionNote
          key={questionId}
          note={entry.note}
          onChange={(text) => {
            dispatch({ type: 'SET_NOTE', questionId, text });
          }}
        />

        {revealed && state.policy.showExplanationAfterAnswer && question.explanation !== undefined && (
          <section className="explanation" aria-label={t('session.explanation')}>
            <h2>{t('session.explanation')}</h2>
            {question.explanation.summary !== undefined && (
              <ExamText lang={question.content.language}>{question.explanation.summary}</ExamText>
            )}
            {question.explanation.detail !== undefined && (
              <ExamText lang={question.content.language}>{question.explanation.detail}</ExamText>
            )}
          </section>
        )}
      </article>
      )}

      <div className="session__controls">
        <Button
          disabled={!can(state, 'PREVIOUS')}
          onClick={() => {
            dispatch({ type: 'PREVIOUS' });
          }}
        >
          {t('session.previous')}
        </Button>

        <Button
          disabled={!can(state, 'TOGGLE_MARK')}
          aria-pressed={entry.markedForReview}
          onClick={() => {
            dispatch({ type: 'TOGGLE_MARK', questionId });
          }}
        >
          {entry.markedForReview ? t('session.marked') : t('session.mark')}
        </Button>

        <Button
          variant="quiet"
          onClick={() => {
            setReporting(true);
          }}
        >
          {t('session.report')}
        </Button>

        <Button
          variant="primary"
          disabled={!can(state, 'NEXT')}
          onClick={() => {
            dispatch({ type: 'NEXT' });
          }}
        >
          {t('session.next')}
        </Button>
      </div>

      <nav className="navigator" aria-label={t('session.navigator')}>
        <p className="navigator__title">{t('session.navigator')}</p>

        {/* VCE opens a separate "Review All / Marked / Incomplete" screen for this. The navigator
            already is that screen and is always on display, so it is a filter rather than a place. */}
        <div className="navigator__filters" role="group" aria-label={t('session.review')}>
          {(['all', 'marked', 'unanswered'] as const).map((candidate) => (
            <button
              key={candidate}
              type="button"
              className={`chip${filter === candidate ? ' chip--selected' : ''}`}
              aria-pressed={filter === candidate}
              onClick={() => {
                setFilter(candidate);
              }}
            >
              {candidate === 'all'
                ? t('session.reviewAll', { count: counts.total })
                : candidate === 'marked'
                  ? t('session.reviewMarked', { count: counts.marked })
                  : t('session.reviewUnanswered', { count: counts.unanswered })}
            </button>
          ))}

          <Button
            variant="quiet"
            disabled={nextMatching(state, filter) === null}
            onClick={() => {
              const to = nextMatching(state, filter);
              if (to !== null) {
                dispatch({ type: 'NAVIGATE', to });
              }
            }}
          >
            {t('session.goToNext')}
          </Button>
        </div>

        {items.length === 0 && <p className="navigator__empty">{t('session.reviewEmpty')}</p>}

        <ol className="navigator__list">
          {items.map((item) => (
            <li key={item.questionId}>
              <button
                type="button"
                className={`navigator__item navigator__item--${item.status}`}
                aria-current={item.current ? 'true' : undefined}
                onClick={() => {
                  dispatch({ type: 'NAVIGATE', to: item.index });
                }}
              >
                <span aria-hidden="true">{item.index + 1}</span>
                <span className="visually-hidden">
                  {t('session.question', { position: item.index + 1, total: counts.total })},{' '}
                  {item.current
                    ? t('session.status.current')
                    : item.answered
                      ? t('session.status.answered')
                      : t('session.status.unanswered')}
                  {item.markedForReview ? `, ${t('session.status.marked')}` : ''}
                </span>
              </button>
            </li>
          ))}
        </ol>

        <p className="navigator__legend" aria-hidden="true">
          <span>
            <span className="navigator__swatch navigator__swatch--current" />
            {t('session.status.current')}
          </span>
          <span>
            <span className="navigator__swatch navigator__swatch--answered" />
            {t('session.status.answered')}
          </span>
          <span>
            <span className="navigator__swatch navigator__swatch--marked" />
            {t('session.status.marked')}
          </span>
        </p>

        {/* Submitting belongs here rather than floating below: this card is the overview of every
            question, and "I am done with all of them" is the action that follows from reading it. */}
        <div className="navigator__submit">
          <Button
            variant="primary"
            onClick={() => {
              dispatch({ type: 'REQUEST_SUBMIT' });
            }}
          >
            {t('session.submit')}
          </Button>

          <Button
            disabled={!can(state, 'PAUSE')}
            onClick={() => {
              dispatch({ type: 'PAUSE' });
            }}
          >
            {t('session.pause')}
          </Button>

          {onSave !== undefined && (
            <Button
              onClick={() => {
                setSaveResult(onSave() ? 'saved' : 'failed');
              }}
            >
              {t('session.save')}
            </Button>
          )}
        </div>

        {saveResult !== null && (
          <p className="navigator__saved" role="status">
            {saveResult === 'saved' ? t('session.saved') : t('session.saveFailed')}
          </p>
        )}
      </nav>

      {state.status === 'confirming-submit' && (
        <SubmitDialog
          unanswered={counts.unanswered}
          marked={counts.marked}
          onCancel={() => {
            dispatch({ type: 'CANCEL_SUBMIT' });
          }}
          onConfirm={() => {
            dispatch({ type: 'CONFIRM_SUBMIT' });
          }}
        />
      )}

      {/* Rendered on top of the screen rather than instead of it. Nothing below unmounts, so
          closing returns to the same scroll position, the same open calculator, the same
          everything — and the session never knew the difference. */}
      {focus && (
        <FocusOverlay
          question={question}
          state={state}
          dispatch={dispatch}
          onClose={onExitFocus}
        />
      )}

      {calculating && (
        <Calculator
          onClose={() => {
            setCalculating(false);
          }}
        />
      )}

      {reporting && (
        <ReportQuestionDialog
          questionId={questionId}
          existing={state.reports.find((report) => report.questionId === questionId)}
          onCancel={() => {
            setReporting(false);
          }}
          onSave={(report) => {
            dispatch({ type: 'REPORT', report });
            setReporting(false);
          }}
        />
      )}
    </section>
  );
}

/**
 * Confirmation before submitting.
 *
 * It states what is unfinished rather than asking a vague "are you sure?" — the number of blank and
 * marked questions is exactly the information someone needs to decide, and going back is one click.
 */
function SubmitDialog({
  unanswered,
  marked,
  onCancel,
  onConfirm,
}: {
  readonly unanswered: number;
  readonly marked: number;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}) {
  const { t } = useI18n();
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    confirmRef.current?.focus();
  }, []);

  return (
    <div className="dialog-backdrop" role="presentation">
      <div className="dialog" role="alertdialog" aria-modal="true" aria-labelledby="submit-title">
        <h2 id="submit-title">{t('submit.title')}</h2>

        {unanswered === 0 && marked === 0 ? (
          <p>{t('submit.complete')}</p>
        ) : (
          <ul>
            {unanswered > 0 && <li>{t('submit.incomplete', { count: unanswered })}</li>}
            {marked > 0 && <li>{t('submit.marked', { count: marked })}</li>}
          </ul>
        )}

        <div className="actions">
          <Button variant="primary" ref={confirmRef} onClick={onConfirm}>
            {t('submit.confirm')}
          </Button>
          <Button onClick={onCancel}>{t('submit.cancel')}</Button>
        </div>
      </div>
    </div>
  );
}
